<template>
  <div class="home">
    <div class="home__head">
      <div>
        <h1 class="home__title">{{ greeting }}</h1>
        <div class="home__sub">{{ subtitle }}</div>
      </div>
      <div class="home__controls">
        <div v-if="kind === 'owner'" class="home__periods">
          <button
            v-for="p in periods"
            :key="p.key"
            class="home__tab home__tab--period"
            :class="{ 'is-active': period === p.key }"
            @click="load(kind, p.key)"
          >{{ p.label }}</button>
        </div>
        <div v-if="canSwitch" class="home__switch">
        <button
          v-for="v in views"
          :key="v.key"
          class="home__tab"
          :class="{ 'is-active': kind === v.key }"
          @click="load(v.key)"
          >{{ v.label }}</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="home__state">
      <i class="el-icon-loading"></i> Učitavanje…
    </div>
    <div v-else-if="error" class="home__state home__state--error">
      Podatke trenutno nije moguće učitati.
    </div>

    <component v-else-if="block" :is="component" :data="block" class="home__body" />
  </div>
</template>

<script>
import DataService from "@/services/data.service";
import AuthService from "@/services/auth.service";
import { canSwitchDashboards } from "@/models/admin";
import IntakeDash from "@/components/dashboards/IntakeDash";
import FinanceDash from "@/components/dashboards/FinanceDash";
import LegalDash from "@/components/dashboards/LegalDash";
import OwnerDash from "@/components/dashboards/OwnerDash";

const TITLES = {
  intake: { label: "Prijem", sub: "moji predmeti i šta traži pažnju" },
  finance: { label: "Finansije", sub: "naplata, isplate i zatvaranje" },
  legal: { label: "Sporazumi i sud", sub: "sudski postupci, sporazumi i prigovori" },
  owner: { label: "Pregled", sub: "poslovanje, otkup i učinak tima" },
};

export default {
  name: "Dashboard",
  components: { IntakeDash, FinanceDash, LegalDash, OwnerDash },
  data() {
    return {
      kind: "",
      payload: null,
      loading: true,
      error: false,
      views: Object.keys(TITLES).map((k) => ({ key: k, label: TITLES[k].label })),
      period: "month",
      periods: [
        { key: "month", label: "Ovaj mjesec" },
        { key: "30d", label: "Zadnjih 30 dana" },
        { key: "prev_month", label: "Prošli mjesec" },
        { key: "year", label: "Ova godina" },
      ],
    };
  },
  computed: {
    block() {
      return this.payload ? this.payload[this.kind] : null;
    },
    component() {
      return {
        intake: "IntakeDash",
        finance: "FinanceDash",
        legal: "LegalDash",
        owner: "OwnerDash",
      }[this.kind];
    },
    // Only listed accounts can look at other people's dashboards.
    canSwitch() {
      return canSwitchDashboards();
    },
    greeting() {
      const user = AuthService.getUser() || {};
      const h = new Date().getHours();
      const part = h < 11 ? "Dobro jutro" : h < 18 ? "Dobar dan" : "Dobro veče";
      return user.first_name ? `${part}, ${user.first_name}` : part;
    },
    subtitle() {
      const base = (TITLES[this.kind] || {}).sub || "";
      if (this.kind === "owner" && this.payload && this.payload.owner) {
        return base + " · " + this.payload.owner.period_label.toLowerCase();
      }
      return base;
    },
  },
  methods: {
    // No type on first load: the server picks the one assigned to this account.
    async load(type, period) {
      this.loading = true;
      this.error = false;
      if (period) this.period = period;
      try {
        const params = [];
        if (type) params.push("type=" + type);
        params.push("period=" + this.period);
        const path = "dashboard?" + params.join("&");
        const res = await DataService.get(path, { nopagination: true });
        this.payload = res.data.data;
        this.kind = this.payload.tip;
        if (this.payload.owner && this.payload.owner.period) {
          this.period = this.payload.owner.period;
        }
      } catch (e) {
        this.error = true;
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style lang="scss" scoped>
.home {
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  min-height: 0;
}

.home__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
}

.home__title {
  font-size: var(--fs-title);
  font-weight: 700;
  color: var(--ink);
  margin: 0;
}

.home__sub {
  font-size: var(--fs-sm);
  color: var(--ink-3);
  margin-top: 4px;
}

.home__controls {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.home__periods,
.home__switch {
  display: flex;
  gap: 6px;
}

.home__switch {
  padding-left: 14px;
  border-left: 1px solid var(--line-strong);
}

.home__tab--period.is-active {
  background: var(--brand);
  border-color: var(--brand);
}

.home__tab {
  padding: 5px 12px;
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  background: var(--surface);
  font: inherit;
  font-size: var(--fs-sm);
  color: var(--ink-2);
  cursor: pointer;

  &:hover {
    background: var(--surface-alt);
  }

  &.is-active {
    background: var(--navy);
    border-color: var(--navy);
    color: #fff;
    font-weight: 500;
  }
}

.home__body {
  flex-grow: 1;
  min-height: 0;
}

.home__state {
  padding: 40px;
  text-align: center;
  font-size: var(--fs-md);
  color: var(--ink-3);
}

.home__state--error {
  color: var(--danger);
}
</style>
